import React, { useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import DeleteConfirmModal from "../AlertComponents/DeleteConfirmModal";
import MoreVertIcon from "@mui/icons-material/MoreVert";
// import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import { BiUpvote } from "react-icons/bi";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import LockIcon from "@mui/icons-material/Lock";
import PeopleIcon from "@mui/icons-material/People";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LinkIcon from "@mui/icons-material/Link";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BlockIcon from "@mui/icons-material/Block";
import { theme } from "../../theme/colors";
import { formatDistanceToNow } from "date-fns";
import { PostType } from "../../utils/feedApi";
import { canLogPostAction } from "../../utils/accountTypes";
import { BLOCK_CONFIRM_DESCRIPTION } from "../../utils/discoverApi";
import { buildBillSlug } from "../../utils/utils";
import { getTopicDisplayLabel } from "../../utils/topicLabels";
import { formatPostLocationSingleLine, getPostLocationMapUrl } from "../../utils/postLocationDisplay";
import PostTypeChip from "./PostTypeChip";
import PostCommentsSection from "./PostCommentsSection";
import PollVoteBlock from "./PollVoteBlock";
import IncognitoAvatar from "./IncognitoAvatar";
import ActionAggregationCard from "./ActionAggregationCard";

const actionBtnSx = {
  textTransform: "none",
  fontWeight: 400,
  minWidth: 44,
  minHeight: 44,
  px: 1,
  fontSize: "14px",
  bgcolor: "transparent",
  color: "#0f172a",
  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
};

export default function PostCard({
  post,
  currentUserId,
  verifiedCountsOnly = false,
  onUpvote,
  onFollow,
  onAuthRequired,
  onVerifyRequired,
  isVerified = true,
  onEdit,
  onDelete,
  onReport,
  onBlock,
  onActionCountChange,
  onActionError,
  defaultCommentsExpanded = false,
  isDetailView = false,
}) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [commentsExpanded, setCommentsExpanded] = useState(defaultCommentsExpanded);
  const serverCommentCount = post?.comment_count ?? post?.commentCount ?? 0;
  const serverVerifiedCommentCount = post?.verified_comment_count ?? 0;
  const [localCommentCount, setLocalCommentCount] = useState(serverCommentCount);
  const [localVerifiedCommentCount, setLocalVerifiedCommentCount] = useState(serverVerifiedCommentCount);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [blocking, setBlocking] = useState(false);

  useEffect(() => {
    setLocalCommentCount(serverCommentCount);
    setLocalVerifiedCommentCount(serverVerifiedCommentCount);
  }, [serverCommentCount, serverVerifiedCommentCount]);

  if (!post) return null;

  const isAnonymousPost = !!post.anonymous;
  const isLoggedIn = !!currentUserId;
  const isAuthor = post.viewer_is_author ?? (isLoggedIn && (post.user?.id === currentUserId || post.user_id === currentUserId));
  const showOverflowMenu = isAuthor || isLoggedIn;

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleCardClick = () => {
    if (!isDetailView) {
      navigate(`/community/post/${post.id}`);
    }
  };

  const authorName = isAnonymousPost
    ? "Anonymous"
    : post.user?.entity_name || `${post.user?.first_name || ""} ${post.user?.last_name || ""}`.trim() || "Anonymous";
  const displayAuthorName = isAnonymousPost && isAuthor ? "Anonymous (you)" : authorName;
  const authorIsVerified = !!post.user?.is_verified;
  const timeAgo = post.created_at ? formatDistanceToNow(new Date(post.created_at), { addSuffix: true }) : "";
  const districtLabel = isAnonymousPost
    ? ""
    : post.user?.district_number ? `${post.user?.state || ""}-${post.user?.district_number || ""}` : post.user?.districtLabel || "";
  const isIdea = post.post_type === PostType.IDEA;
  const hasActionStep = canLogPostAction(post);
  const authorProfileId = isAnonymousPost ? null : post.user?.id || post.user_id;

  const displayFollowCount = verifiedCountsOnly
    ? (post.verified_follow_count ?? 0)
    : (post.follower_count || post.follow_count || 0);
  const displayUpvoteCount = verifiedCountsOnly
    ? (post.verified_upvote_count ?? 0)
    : (post.upvote_count || post.upvoteCount || 0);
  const displayCommentCount = verifiedCountsOnly
    ? localVerifiedCommentCount
    : localCommentCount;

  const contextLabelSx = {
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: 0.35,
    display: "block",
  };

  const renderContext = () => {
    const locationLine = formatPostLocationSingleLine(post);
    const hasLinks = post.bill || post.event || post.external_link_url;
    if (!locationLine && !hasLinks) return null;

    const linkSx = {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      mt: 0.25,
      mb: 0.25,
      textDecoration: "none",
      cursor: "pointer",
      "&:hover": { opacity: 0.85 },
    };

    if (post.external_link_url) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {locationLine && (
            <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
                <Typography component="span" sx={contextLabelSx}>Location</Typography>
                <Typography
                  component="a"
                  href={getPostLocationMapUrl(post) || "#"}
                  onClick={(e) => e.stopPropagation()}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 14,
                    color: "#0f172a",
                    lineHeight: 1.45,
                    fontWeight: 500,
                    textDecoration: "none",
                    display: "inline-block",
                    "&:hover": { textDecoration: "underline", color: "#0f172a" },
                  }}
                >
                  {locationLine}
                </Typography>
            </Box>
          )}
          <Box>
            <Typography component="span" sx={contextLabelSx}>External link</Typography>
            <Box component="a" href={post.external_link_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={linkSx}>
              <LinkIcon sx={{ fontSize: 18, color: theme.primary }} />
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }}>
                {post.external_link_title || post.external_link_url}
              </Typography>
              <OpenInNewIcon sx={{ fontSize: 14, color: theme.primary }} />
            </Box>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        {locationLine && (
          <Box sx={{ minWidth: 0 }} onClick={(e) => e.stopPropagation()}>
              <Typography component="span" sx={contextLabelSx}>Location</Typography>
              <Typography
                component="a"
                href={getPostLocationMapUrl(post) || "#"}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 14,
                  color: "#0f172a",
                  lineHeight: 1.45,
                  fontWeight: 500,
                  textDecoration: "none",
                  display: "inline-block",
                  "&:hover": { textDecoration: "underline", color: "#0f172a" },
                }}
              >
                {locationLine}
              </Typography>
          </Box>
        )}

        {post.bill && (() => {
          const slug = buildBillSlug(post.bill);
          const typeDisplay = (post.bill.type || "").toUpperCase().trim();
          const numDisplay = post.bill.bill_number != null ? String(post.bill.bill_number) : "";
          const billLabel = [typeDisplay, numDisplay].filter(Boolean).join(" ");
          const titleSnippet = post.bill.title ? post.bill.title.substring(0, 50) + (post.bill.title.length > 50 ? "..." : "") : "";
          const displayText = billLabel ? `${billLabel}${titleSnippet ? " - " + titleSnippet : ""}` : titleSnippet || "View bill";

          return (
            <Box key="bill">
              <Typography component="span" sx={contextLabelSx}>Link to bill</Typography>
              <Box
                component={RouterLink}
                to={`/bills/${slug}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ ...linkSx, color: theme.primary }}
              >
                <ArticleOutlinedIcon sx={{ fontSize: 18, color: theme.primary }} />
                <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }}>
                  {displayText}
                </Typography>
              </Box>
            </Box>
          );
        })()}

        {post.event && (
          <Box>
            <Typography component="span" sx={contextLabelSx}>Link to event</Typography>
            <Box
              component={RouterLink}
              to={`/events/${post.event.id}`}
              onClick={(e) => e.stopPropagation()}
              sx={{ ...linkSx, color: theme.primary }}
            >
              <EventOutlinedIcon sx={{ fontSize: 18, color: theme.primary }} />
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: theme.primary }}>
                {post.event.title || "Event"}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{
      bgcolor: "#ffffff",
      borderRadius: "12px",
      border: "1px solid #e5e7eb",
      p: 2,
      mb: 2,
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    }}>
      {/* Clickable content area */}
      <Box
        onClick={handleCardClick}
        sx={{ cursor: isDetailView ? "default" : "pointer" }}
      >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, position: "relative" }}>
        {(() => {
          const authorMetaRow = (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              {districtLabel && (
                <>
                  <Typography sx={{ fontSize: "13px", color: "#64748b" }}>{districtLabel}</Typography>
                  <Typography sx={{ fontSize: "13px", color: "#cbd5e1" }}>•</Typography>
                </>
              )}
              <Typography sx={{ fontSize: "13px", color: "#64748b" }}>{timeAgo}</Typography>
              {post.visibility && post.visibility !== "public" && (
                <>
                  <Typography sx={{ fontSize: "13px", color: "#cbd5e1" }}>•</Typography>
                  {post.visibility === "followers" ? <PeopleIcon sx={{ fontSize: 14, color: "#64748b" }} /> : <LockIcon sx={{ fontSize: 14, color: "#64748b" }} />}
                </>
              )}
              {post.geo_target && post.geo_target !== "everyone" && (
                <>
                  <Typography sx={{ fontSize: "13px", color: "#cbd5e1" }}>•</Typography>
                  <LocationOnIcon sx={{ fontSize: 14, color: "#64748b" }} />
                  <Typography sx={{ fontSize: "13px", textTransform: "capitalize", color: "#64748b" }}>
                    {post.geo_target === "district" ? `${post.geo_target_state}-${post.geo_target_district}` : post.geo_target_state}
                  </Typography>
                </>
              )}
              {post.isSample && (
                <Box sx={{ bgcolor: "#f3f4f6", px: 0.75, py: 0.25, borderRadius: 1, ml: 0.5 }}>
                  <Typography sx={{ fontSize: "10px", color: "#0f172a", fontStyle: "italic" }}>Sample post</Typography>
                </Box>
              )}
              {post.pinnedByCivixThread && (
                <PushPinIcon sx={{ fontSize: 14, color: theme.primary, ml: 0.5 }} />
              )}
            </Box>
          );
          const authorInner = (
            <>
              {isAnonymousPost ? (
                <IncognitoAvatar size={40} verified={!!post.author_is_verified} />
              ) : (
                <Avatar
                  src={post.user?.profile_image}
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: `${theme.primary}20`,
                    color: theme.primary,
                    fontWeight: 600,
                    fontSize: "14px",
                    boxSizing: "border-box",
                    ...(authorIsVerified ? { border: "2px solid #d4af37" } : {}),
                  }}
                >
                  {authorName.charAt(0).toUpperCase()}
                </Avatar>
              )}
              <Box>
                <Typography sx={{ fontSize: "15px", fontWeight: 600, color: "#111", lineHeight: 1.2, mb: 0.5 }}>
                  {displayAuthorName}
                </Typography>
                {authorMetaRow}
              </Box>
            </>
          );
          return authorProfileId ? (
            <Box
              component={RouterLink}
              to={`/profile/${authorProfileId}`}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "flex",
                gap: 1.5,
                pr: 8,
                textDecoration: "none",
                color: "inherit",
                alignItems: "flex-start",
                "&:hover": { opacity: 0.9 },
              }}
            >
              {authorInner}
            </Box>
          ) : (
            <Box sx={{ display: "flex", gap: 1.5, pr: 8 }}>{authorInner}</Box>
          );
        })()}

        {/* Post Type Chip (Top Right) */}
        <Box sx={{ position: "absolute", top: 0, right: 0, display: "flex", alignItems: "center", gap: 1 }}>
          <PostTypeChip postType={post.post_type} />
          {isIdea && post.idea_scope && (
            <Box sx={{ bgcolor: `${theme.primary}10`, px: 1, py: 0.25, borderRadius: "10px" }}>
              <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#0f172a", textTransform: "capitalize" }}>
                {post.idea_scope}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ 
          fontSize: "15px", 
          color: "#111827", 
          whiteSpace: "pre-wrap", 
          wordBreak: "break-word", 
          lineHeight: 1.5,
          ...(!isDetailView ? {
            display: "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          } : {}),
        }}>
          {post.content}
        </Typography>
        {!isDetailView && post.content && post.content.length > 200 && (
          <Typography sx={{ fontSize: "13px", color: theme.primary, fontWeight: 500, mt: 0.75, cursor: "pointer" }}>
            Read more
          </Typography>
        )}
      </Box>

      {post.poll_options?.length > 0 && (
        <Box onClick={(e) => e.stopPropagation()}>
          <PollVoteBlock
            post={post}
            currentUserId={currentUserId}
            onAuthRequired={onAuthRequired}
            verifiedCountsOnly={verifiedCountsOnly}
          />
        </Box>
      )}

      {renderContext()}

      {/* Image Attachment */}
      {(post.image_url || post.imageUrl) && (
        <Box sx={{ mb: 1.5, borderRadius: "12px", overflow: "hidden", bgcolor: "#f3f4f6" }}>
          <img src={post.image_url || post.imageUrl} alt="Post attachment" style={{ width: "100%", maxHeight: 260, objectFit: "cover" }} />
        </Box>
      )}

      {/* Topics */}
      {post.topics && post.topics.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            mt: 1.5,
            mb: hasActionStep
              ? (post.bill ? 1 : 1.5)
              : 1.5,
          }}
        >
          {post.topics.slice(0, 3).map((topic, idx) => (
            <Box key={idx} sx={{ bgcolor: "#f3f4f6", px: 1.25, py: 0.5, borderRadius: "12px" }}>
              <Typography sx={{ fontSize: "11px", color: "#0f172a" }}>
                {getTopicDisplayLabel(typeof topic === "object" ? topic?.id ?? topic : topic)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      </Box>{/* End clickable content area */}

      {hasActionStep && (
        <Box
          sx={{
            mt: post.topics?.length
              ? 0
              : post.bill
                ? 1.25
                : 0.75,
          }}
        >
          <ActionAggregationCard
            post={post}
            isAuthor={isAuthor}
            isVerified={isVerified}
            verifiedCountsOnly={verifiedCountsOnly}
            currentUserId={currentUserId}
            onAuthRequired={onAuthRequired}
            onActionCountChange={(change) => onActionCountChange?.(post, change)}
            onError={onActionError}
          />
        </Box>
      )}

      <Divider
        sx={{
          mt: hasActionStep ? 0.5 : 1.5,
          mb: hasActionStep ? 0 : 1,
          borderColor: "#f3f4f6",
        }}
      />

      {/* Actions Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: hasActionStep ? 1 : 0,
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {!isAuthor && (
            <Button
              aria-label={post.has_followed ? "Unsave post" : "Save post"}
              startIcon={post.has_followed ? <BookmarkIcon sx={{ fontSize: 20, color: "inherit" }} /> : <BookmarkBorderIcon sx={{ fontSize: 20, color: "inherit" }} />}
              onClick={() => onFollow(post)}
              sx={{
                ...actionBtnSx,
                color: post.has_followed ? theme.primary : "#0f172a",
                fontWeight: post.has_followed ? 600 : 400,
              }}
            >
              {displayFollowCount}
            </Button>
          )}

          <Button
            startIcon={<BiUpvote sx={{ fontSize: 22, color: "inherit" }} />}
            onClick={() => !isAuthor && onUpvote?.(post)}
            sx={{
              ...actionBtnSx,
              color: (post.has_upvoted ?? post.viewer_has_upvoted) ? theme.primary : "#0f172a",
              fontWeight: (post.has_upvoted ?? post.viewer_has_upvoted) ? 600 : 400,
              cursor: isAuthor ? "default" : "pointer",
              opacity: isAuthor ? 0.45 : 1,
            }}
          >
            {displayUpvoteCount}
          </Button>

          <Button
            startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 20, color: "inherit" }} />}
            onClick={() => {
              if (!currentUserId) {
                onAuthRequired?.();
                return;
              }
              setCommentsExpanded((prev) => !prev);
            }}
            sx={{
              ...actionBtnSx,
              color: "#0f172a",
            }}
          >
            {displayCommentCount}
          </Button>
        </Box>

        {showOverflowMenu && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={handleMenuOpen}
              aria-label="Post actions"
              sx={{ color: "#0f172a", p: 1 }}
            >
              <MoreVertIcon sx={{ fontSize: 20, color: "inherit" }} />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Impact Microcopy */}
      {post.impactActionCount > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #f9fafb" }}>
          <Typography sx={{ fontSize: "11px", color: "#64748b", fontStyle: "italic" }}>
            {post.impactActionCount} {post.impactActionCount === 1 ? "person" : "people"} logged an action after seeing this.
          </Typography>
        </Box>
      )}

      {/* Inline Comments */}
      {commentsExpanded && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid #f1f5f9" }}>
          <PostCommentsSection
            postId={post.id}
            currentUserId={currentUserId}
            verifiedCountsOnly={verifiedCountsOnly}
            onAuthRequired={onAuthRequired}
            onCommentCountChange={(totalCount, verifiedCount) => {
              setLocalCommentCount(totalCount);
              if (typeof verifiedCount === "number") {
                setLocalVerifiedCommentCount(verifiedCount);
              }
            }}
          />
        </Box>
      )}

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: { borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }
        }}
      >
        {isAuthor ? [
          <MenuItem key="edit" onClick={() => { handleMenuClose(); onEdit?.(post); }} sx={{ py: 1.5, px: 2, gap: 1.5 }}>
            <EditOutlinedIcon sx={{ fontSize: 20, color: "#111" }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#111" }}>Edit Post</Typography>
          </MenuItem>,
          <MenuItem key="delete" onClick={() => { handleMenuClose(); setShowDeleteConfirm(true); }} sx={{ py: 1.5, px: 2, gap: 1.5 }}>
            <DeleteOutlineIcon sx={{ fontSize: 20, color: "#ef4444" }} />
            <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#ef4444" }}>Delete Post</Typography>
          </MenuItem>,
        ] : isLoggedIn ? [
          <MenuItem key="report" onClick={() => { handleMenuClose(); onReport?.(post); }} sx={{ py: 1.5, px: 2, gap: 1.5 }}>
            <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#ef4444" }}>Report Post</Typography>
          </MenuItem>,
          ...(onBlock && authorProfileId ? [
            <MenuItem key="block" onClick={() => { handleMenuClose(); setShowBlockConfirm(true); }} sx={{ py: 1.5, px: 2, gap: 1.5 }}>
              <BlockIcon sx={{ fontSize: 20, color: "#ef4444" }} />
              <Typography sx={{ fontSize: "15px", fontWeight: 500, color: "#ef4444" }}>Block User</Typography>
            </MenuItem>,
          ] : []),
        ] : []}
      </Menu>

      <DeleteConfirmModal
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => { setShowDeleteConfirm(false); onDelete?.(post); }}
      />

      <DeleteConfirmModal
        open={showBlockConfirm}
        onClose={() => setShowBlockConfirm(false)}
        title={`Block ${authorName}?`}
        description={BLOCK_CONFIRM_DESCRIPTION}
        confirmLabel="Block"
        loading={blocking}
        onConfirm={async () => {
          setBlocking(true);
          try {
            await onBlock?.({ userId: authorProfileId, name: authorName, post });
            setShowBlockConfirm(false);
          } catch {
            // Keep the dialog open so the user can retry or cancel.
          } finally {
            setBlocking(false);
          }
        }}
      />
    </Box>
  );
}
